import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "./queryKeys";
import { unicodeCaseFold } from "./unicodeCaseFold";
import type { VariableResponse } from "./variableCascade";
import {
  fetchAllVariablePersonas,
  fetchAllVariableScenarios,
} from "./variableScopeOptions";

/** Resolve owner names only when key + scope cannot distinguish declarations. */
export function useVariableScopeLabels(
  agentId: string,
  variables: readonly VariableResponse[],
) {
  const repeatedScopes = new Set<string>();
  const seen = new Set<string>();
  for (const variable of variables) {
    const identity = `${variable.scope_type}:${unicodeCaseFold(variable.key)}`;
    if (seen.has(identity)) repeatedScopes.add(variable.scope_type);
    seen.add(identity);
  }
  const personas = useQuery({
    enabled: !!agentId && repeatedScopes.has("persona"),
    queryKey: queryKeys.personas.allForAgent(agentId),
    queryFn: ({ signal }) => fetchAllVariablePersonas(agentId, signal),
    refetchOnWindowFocus: true,
  });
  const scenarios = useQuery({
    enabled: !!agentId && repeatedScopes.has("scenario"),
    queryKey: queryKeys.scenarios.allForAgent(agentId),
    queryFn: ({ signal }) => fetchAllVariableScenarios(agentId, signal),
    refetchOnWindowFocus: true,
  });
  const owners = new Map<string, string>();
  for (const owner of [
    ...(personas.isError ? [] : (personas.data ?? [])),
    ...(scenarios.isError ? [] : (scenarios.data ?? [])),
  ]) {
    if (owner.agent_id === agentId) owners.set(owner.id, owner.display_name);
  }
  const labels = new Map<string, string>();
  for (const variable of variables) {
    const name = owners.get(variable.scope_id);
    if (name) labels.set(variable.id, `${variable.scope_type}: ${name}`);
  }
  const isPending =
    (repeatedScopes.has("persona") && personas.isPending) ||
    (repeatedScopes.has("scenario") && scenarios.isPending);
  const isError =
    (repeatedScopes.has("persona") && personas.isError) ||
    (repeatedScopes.has("scenario") && scenarios.isError);
  return { labels, isPending, isError };
}
